import Vue from 'vue'
import { BootstrapVue, ModalPlugin, ToastPlugin } from 'bootstrap-vue'
import FormWizard from 'vue-form-wizard'
import VueAutosuggest from 'vue-autosuggest'
import VueCompositionAPI from '@vue/composition-api'
import VueHtmlToPaper from 'vue-html-to-paper'
import _ from 'lodash'
import Speech from 'speak-tts'
import { createPinia, PiniaVuePlugin } from 'pinia';
import '@/registerServiceWorker'
import RouterTab from 'vue-router-tab'
import VueGoodTablePlugin from 'vue-good-table'
import moment from 'moment'
import VueMoment from 'vue-moment'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import VueTheMask from 'vue-the-mask'
import VueFullscreen from 'vue-fullscreen'
import 'img-comparison-slider'

import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import 'vue-router-tab/dist/lib/vue-router-tab.css'
import 'highlight.js/styles/github-gist.css'
import InputDateTime from '@/components/InputDateTime.vue'
import { Icon } from '@iconify/vue2'
import DekaToast from '@core/plugins/dekatoast'

/* import i18n from '@/libs/i18n' */
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate'
import { image, mimes, size, min, max, max_value, min_value, numeric, digits } from 'vee-validate/dist/rules'
import { isValidDateInput } from '@/utils/dateInput'
import { initDb } from '@core/idb/indexDb'
import VueHotkey from 'v-hotkey'
import router from './router'
import store from './store'
import App from './App.vue'

// Global Components
import './global-components'
// 3rd party plugins
import axios from '@/libs/axios'
import '@/libs/acl'
import '@/libs/portal-vue'
import '@/libs/clipboard'
import '@/libs/toastification'
import '@/libs/sweet-alerts'
import '@/libs/vue-select'
import printConsoleBanner from '@/libs/console-banner'
import useJwt from './@core/auth/jwt/useJwt'
import { initialAbility } from './libs/acl/config'
import sLocalStorage from './@core/utils/secure-storage'

import hljs from 'highlight.js/lib/core';
import hljsJson from 'highlight.js/lib/languages/json';

import preloadAudio from './preloadAudio'
import { useAuthStore } from './pinia/auth'
import VueSignaturePad from 'vue-signature-pad';
import printJS from 'print-js';
import ThermalPrinterService from '@/services/thermal-printer';

Vue.use(VueSignaturePad);

printConsoleBanner()
preloadAudio()
extend('image', image)
extend('size', size)
extend('mimes', mimes)
extend('min', min)
extend('max', max)
extend('max_value', max_value)
extend('min_value', min_value)
extend('numeric', numeric)
extend('digits', digits)
extend('date_ddmmyyyy', {
  validate: (value) => value === null || value === undefined || value === '' || isValidDateInput(value),
  message: 'Format {_field_} harus dd/mm/yyyy, contoh: 31/12/2027'
})

initDb()
require('moment/locale/id')
moment.locale('id')

const optionPrint = {
  name: '_blank',
  specs: ['fullscreen=no', 'titlebar=no', 'scrollbars=no', 'width=0', 'height=0', 'left=-9999', 'top=-9999'],
  styles: [
    'https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css'
  ],
  timeout: 100, // reduce timeout for faster printing
  autoClose: true, // automatically close after printing
  windowTitle: 'Print' // override the window title
}

hljs.registerLanguage('json', hljsJson);

// BSV Plugin Registration
Vue.component('InputDateTime', InputDateTime)
Vue.use(ToastPlugin)
Vue.use(VueHtmlToPaper, optionPrint)
Vue.use(ModalPlugin)
Vue.use(BootstrapVue)
Vue.use(RouterTab)
Vue.use(VueGoodTablePlugin)
Vue.use(FormWizard)
Vue.use(VueAutosuggest)
Vue.use(ToastificationContent)
Vue.use(VueFullscreen)
Vue.use(VueHotkey)
Vue.use(VueTheMask)
Vue.use(PiniaVuePlugin)
Vue.use(hljs.vuePlugin);

// Register DekaToast - unified toast system
Vue.use(DekaToast)

Vue.component('ValidationProvider', ValidationProvider)
Vue.component('ValidationObserver', ValidationObserver)
Vue.component('Icon', Icon)

// Composition API
Vue.use(VueCompositionAPI)

// Feather font icon - For form-wizard
// * Shall remove it if not using font-icons of feather-icons - For form-wizard
require('@core/assets/fonts/feather/iconfont.css') // For form-wizard
require('@core/assets/fonts/font-awesome-5/css/all.css')

// import core styles
require('@core/scss/core.scss')

// import assets styles
require('@/assets/scss/style.scss')
require('@core/scss/vue/libs/vue-select.scss')

Vue.config.productionTip = false

// Configure Vue to recognize img-comparison-slider web component
Vue.config.ignoredElements = [/img-comparison-slider/]

/**
 * Global error handler for chunk loading failures (CSS/JS assets)
 * This catches errors that occur outside of Vue Router navigation
 */
const handleGlobalChunkError = (message, source) => {
  // Check if this is a chunk loading error
  const isChunkError = (
    (typeof message === 'string' && (
      message.includes('Loading chunk') ||
      message.includes('Loading CSS chunk') ||
      message.includes('ChunkLoadError')
    )) ||
    (source && source.includes('/js/') && message && message.includes('404')) ||
    (source && source.includes('/css/') && message && message.includes('404'))
  )

  if (isChunkError) {
    console.warn('🔄 Asset loading failed, checking for update...')

    // Prevent infinite refresh loop
    const lastRefresh = sessionStorage.getItem('chunk_error_refresh')
    const now = Date.now()

    if (!lastRefresh || (now - parseInt(lastRefresh, 10)) > 30000) {
      sessionStorage.setItem('chunk_error_refresh', now.toString())

      // Clear all caches
      if ('caches' in window) {
        caches.keys().then(names => {
          Promise.all(names.map(name => caches.delete(name)))
            .then(() => {
              window.location.reload(true)
            })
        }).catch(() => {
          window.location.reload(true)
        })
      } else {
        window.location.reload(true)
      }
      return true
    }
  }
  return false
}

// Listen for resource loading errors (CSS, JS files)
window.addEventListener('error', (event) => {
  // Check if it's a resource loading error (script or link element)
  if (event.target && (event.target.tagName === 'SCRIPT' || event.target.tagName === 'LINK')) {
    const src = event.target.src || event.target.href || ''
    if (src.includes('/js/') || src.includes('/css/')) {
      handleGlobalChunkError('Resource failed to load', src)
    }
  }
}, true) // Use capture phase to catch before bubbling

// Listen for unhandled promise rejections (dynamic imports)
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason) {
    const message = event.reason.message || String(event.reason)
    if (handleGlobalChunkError(message, '')) {
      event.preventDefault() // Prevent console error
    }
  }
})

Vue.use(VueMoment, { moment })


Vue.filter('formatDate', (value) => {
  if (!value || value === '0' || value === '0000-00-00' || value === 'null' || value === 0) return '-'
  const momentDate = moment(String(value))
  if (!momentDate.isValid() || momentDate.year() < 1900 || momentDate.year() === 1970) return '-'
  return momentDate.format('DD/MM/YYYY')
})

Vue.filter('formatDateTime', (value) => {
  if (!value || value === '0' || value === '0000-00-00' || value === 'null' || value === 0) return '-'
  const momentDate = moment(String(value))
  if (!momentDate.isValid() || momentDate.year() < 1900 || momentDate.year() === 1970) return '-'
  return momentDate.format('DD/MM/YYYY hh:mm')
})


Vue.prototype.$axios = axios
window.axios = axios
global.axios = axios

try {
  const speech = new Speech()
  const lang = 'id-ID'
  if (speech.hasBrowserSupport()) {
    speech
      .init({
        lang,
        rate: 1,
        pitch: 1,
        volume: 1
      })
      .then((data) => {
        const { voices } = data
        const voice = voices.filter((v) => v.lang === lang)
        if (voice[0]) speech.setVoice(voice[0].name)
        // Voice not found for specified language
        Vue.prototype.$speechVoices = voice.map((v) => v.name)
      })
    Vue.prototype.$speech = speech
  }
} catch (error) {
  // Browser does not support text to speech features
}

const globalHelper = {
  install(Vue, options) {
    Vue.prototype.arrayRuanganToLists = (ruangans) => {
      if (ruangans.length > 0) {
        return ruangans.map((ruangan) => {
          return {
            id: ruangan.id,
            name: ruangan.deskripsi
          }
        })
      }

      return []
    }

    Vue.prototype.arrayToLists = (arrays, customField = false) => {
      if (arrays.length > 0) {
        return arrays.map((data) => {
          let { name } = data

          if (customField && customField.length > 0) {
            switch (customField.length) {
              case 1:
                name = data[customField[0]]
                break
              case 2:
                name = data[customField[0]][customField[1]]
                break
              case 3:
                name = data[customField[0]][customField[1]][customField[2]]
                break
              case 4:
                name =
                  data[customField[0]][customField[1]][customField[2]][
                    customField[3]
                  ]
                break
              default:
                name = null
                break
            }
          }
          return {
            id: data.id,
            name
          }
        })
      }

      return []
    }

    Vue.prototype.toast = (type, message) => {
      const toastMessage = message.message || message
      const toastTitle = message.title || null

      switch (type) {
        case 'success':
          this.$dekatoast.success(toastTitle ? { message: toastMessage, title: toastTitle } : toastMessage)
          break
        case 'danger':
        case 'error':
          this.$dekatoast.error(toastTitle ? { message: toastMessage, title: toastTitle } : toastMessage)
          break
        case 'warning':
          this.$dekatoast.warning(toastTitle ? { message: toastMessage, title: toastTitle } : toastMessage)
          break
        case 'info':
        default:
          this.$dekatoast.info(toastTitle ? { message: toastMessage, title: toastTitle } : toastMessage)
          break
      }
    }

    Vue.prototype.$nikHandler = async (nik) => {
      if (nik.length === 16) {
        const d = Vue.prototype.dateFromNik(nik)
        const date = moment(d.date).format('DD/MM/YYYY')
        const jk = Vue.prototype.getJkFromNik(d)
        const provinsi = Vue.prototype.provinsiFromNik(nik)
        const kota = await Vue.prototype.kotaFromNik(nik)
        const kecamatan = await Vue.prototype.kecamatanFromNik(nik)

        return {
          date,
          jk,
          provinsi,
          kota,
          kecamatan
        }
      }
    }

    Vue.prototype.dateFromNik = (nik) => {
      if (typeof nik === 'number') {
        nik = `${nik}`
      }

      const dateString = nik.substr(6, 6)

      let day = dateString.substr(0, 2)
      const month = dateString.substr(2, 2)
      let year = dateString.substr(4, 2)

      if (day > 40) {
        day -= 40
      }

      if (parseInt(`20${year}`, 10) > new Date().getFullYear()) {
        year = `19${year}`
      } else {
        year = `20${year}`
      }

      return {
        date: new Date(`${year}-${month}-${day}`),
        d: dateString.substr(0, 2),
        m: month,
        y: year
      }
    }

    Vue.prototype.getJkFromNik = (parsedDate) => {
      if (parsedDate.d > 40) {
        return (
          store.getters['masterdata/jk'].find((jk) => jk.value == 2) || null
        )
      }

      return store.getters['masterdata/jk'].find((jk) => jk.value == 1) || null
    }

    Vue.prototype.provinsiFromNik = (nik) => {
      const code = nik.substr(0, 2)

      const provinsi = store.getters['masterdata/provinsi'].find(
        (p) => p.value == code
      )

      return provinsi
    }

    Vue.prototype.kotaFromNik = async (nik) => {
      const code = nik.substr(0, 4)

      const { data } = await axios.get(`wilayah/${code}?jenis=2`)

      if (data.success && data.data) {
        return data.data
      }

      return null
    }

    Vue.prototype.kecamatanFromNik = async (nik) => {
      const code = nik.substr(0, 6)

      const { data } = await axios.get(`wilayah/${code}?jenis=3`)

      if (data.success && data.data) {
        return data.data
      }

      return null
    }

    Vue.prototype.failSafe = (variable, fallback = '') => {
      if (variable === undefined || variable === null) {
        return fallback
      }
      return variable
    }

    Vue.prototype.$printDirect = (elementId, options = {}) => {
      const defaultOptions = {
        printable: elementId,
        type: 'html',
        css: options.styles || ['/print/style.css'],
        scanStyles: false,
        honorMarginPadding: false,
        honorColor: false
      }

      // Merge custom options
      const printOptions = { ...defaultOptions, ...options }

      // For etiket printing, use specific CSS
      if (elementId === 'etiket') {
        printOptions.css = ['/print/etiket.css']
      }

      printJS(printOptions)
    }

    // Initialize thermal printer service
    const thermalPrinter = new ThermalPrinterService()

    // Add thermal printing method
    Vue.prototype.$printThermal = async (receiptData, options = {}) => {
      try {
        const result = await thermalPrinter.printReceipt(receiptData, options)

        if (result.fallback && result.html) {
          // If thermal printing failed, use CSS fallback with generated HTML
          const tempDiv = document.createElement('div')
          tempDiv.id = 'thermal-fallback-' + Date.now()
          tempDiv.innerHTML = result.html
          tempDiv.style.display = 'none'
          document.body.appendChild(tempDiv)

          // Print using existing printDirect method
          Vue.prototype.$printDirect(tempDiv.id, {
            styles: ['/print/pos80.css']
          })

          // Clean up
          setTimeout(() => {
            document.body.removeChild(tempDiv)
          }, 1000)
        }

        return result
      } catch (error) {
        console.error('Thermal printing failed:', error)
        throw error
      }
    }

    // Add e-resep specific thermal printing
    Vue.prototype.$printEResepThermal = async (eresepData, options = {}) => {
      try {
        return await thermalPrinter.printEResep(eresepData, options)
      } catch (error) {
        console.error('E-Resep thermal printing failed:', error)
        throw error
      }
    }

    // Add printer test method
    Vue.prototype.$testThermalPrinter = async () => {
      try {
        return await thermalPrinter.testPrint()
      } catch (error) {
        console.error('Thermal printer test failed:', error)
        throw error
      }
    }

    // Configure thermal printer
    Vue.prototype.$configureThermalPrinter = (config) => {
      thermalPrinter.configure(config)
    }
  }
}

Vue.use(globalHelper)

const pinia = createPinia();
const authStore = useAuthStore(pinia)

authStore.setToken({ access_token: authStore.token, refreshToken: authStore.refreshToken })

const vueIns = new Vue({
  store,
  pinia,
  router,
  /* i18n, */
  render: (h) => h(App)
}).$mount('#app')

// const interceptUnauthorized = axios.interceptors.response.use(
//   (response) => {
//     return response
//   },
//   (err) => {
//     if (_.get(err.response, 'status') === 401) {
//       axios.interceptors.response.eject(interceptUnauthorized) // ejecting the interceptor to avoid looping calls.
//       localStorage.removeItem(useJwt.jwtConfig?.storageTokenKeyName)
//       localStorage.removeItem(useJwt.jwtConfig?.storageRefreshTokenKeyName)
//       sLocalStorage.removeItem('userData')
//       vueIns.$ability.update(initialAbility)
//       if (vueIns.$route.name !== 'auth-login') {
//         vueIns.$router.push({ name: 'auth-login' })
//       }
//       // window.location.reload()
//     }
//     return Promise.reject(err)
//   }
// )
